import { z } from "zod";
import type { ZodObject, ZodRawShape, ZodTypeAny } from "zod";
import type { SchemaField, SchemaGroup } from "../types";

/**
 * Generates a Zod schema from a template's SchemaField definitions.
 * This allows runtime validation of content_config, layout_config, and seo_config
 * based on the template's declared schema.
 */

/**
 * All config values combined for condition evaluation.
 * Keys are in "schema.field" format (e.g., "layout.cta", "content.title")
 */
interface AllConfigs {
	content: Record<string, unknown>;
	layout: Record<string, unknown>;
	seo: Record<string, unknown>;
}

/**
 * Get a value from the combined configs using a path like "layout.cta" or "content.title"
 */
function getValueByPath(configs: AllConfigs, path: string): unknown {
	const [schema, ...fieldParts] = path.split(".");
	const field = fieldParts.join(".");

	if (schema === "layout") return configs.layout[field];
	if (schema === "content") return configs.content[field];
	if (schema === "seo") return configs.seo[field];

	return undefined;
}

/**
 * Check if all conditions in a condition object are met
 */
function evaluateConditions(conditions: Record<string, unknown> | undefined, configs: AllConfigs): boolean {
	if (!conditions || Object.keys(conditions).length === 0) {
		return false;
	}

	// All conditions must be met (AND logic)
	for (const [path, expectedValue] of Object.entries(conditions)) {
		const actualValue = getValueByPath(configs, path);
		if (actualValue !== expectedValue) {
			return false;
		}
	}

	return true;
}

/**
 * Determine if a field is effectively required based on static required flag and conditions
 */
function isFieldRequired(field: SchemaField, configs: AllConfigs): boolean {
	// If statically required, always required
	if (field.required) {
		return true;
	}

	// Check required_if conditions
	if (field.conditions?.required_if) {
		return evaluateConditions(field.conditions.required_if, configs);
	}

	return false;
}

/**
 * Determine if a field should be validated (visible)
 */
function isFieldVisible(field: SchemaField, configs: AllConfigs): boolean {
	// If no visible_if conditions, field is always visible
	if (!field.conditions?.visible_if) {
		return true;
	}

	return evaluateConditions(field.conditions.visible_if, configs);
}

/**
 * Convert a single SchemaField definition to a Zod type
 */
function schemaFieldToZod(field: SchemaField, isRequired: boolean): ZodTypeAny {
	let zodType: ZodTypeAny;

	switch (field.type) {
		case "string":
		case "textarea": {
			// Build the base string schema with validations
			let baseStringSchema = z.string();

			// For required fields, enforce non-empty string
			if (isRequired) {
				baseStringSchema = baseStringSchema.min(1, "This field is required");
			}

			if (field.max !== undefined) {
				baseStringSchema = baseStringSchema.max(field.max, `Maximum ${field.max} characters allowed`);
			}
			if (field.min !== undefined && field.min > 1) {
				baseStringSchema = baseStringSchema.min(field.min, `Minimum ${field.min} characters required`);
			}

			// Handle null values by transforming them to empty string first
			zodType = z.preprocess(
				(val) => (val === null ? "" : val),
				baseStringSchema,
			);
			break;
		}

		case "url": {
			// Stricter URL regex that requires a proper domain with TLD
			const urlRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,}(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;

			// Build the base URL schema
			let baseUrlSchema = z.string().regex(urlRegex, "Must be a valid URL");

			// For required URL fields, enforce valid URL
			if (isRequired) {
				baseUrlSchema = baseUrlSchema.min(1, "This field is required");
			}

			if (field.max !== undefined) {
				baseUrlSchema = baseUrlSchema.max(field.max, `Maximum ${field.max} characters allowed`);
			}

			// Handle null values by transforming them to empty string first
			zodType = z.preprocess(
				(val) => (val === null ? "" : val),
				baseUrlSchema,
			);
			break;
		}

		case "number": {
			let numberSchema = z.number();

			if (field.min !== undefined) {
				numberSchema = numberSchema.min(field.min, `Minimum value is ${field.min}`);
			}
			if (field.max !== undefined) {
				numberSchema = numberSchema.max(field.max, `Maximum value is ${field.max}`);
			}

			zodType = numberSchema;
			break;
		}

		case "boolean": {
			zodType = z.boolean();
			break;
		}

		case "select": {
			// If static options are provided, validate against them
			if (field.options && field.options.length > 0) {
				const validValues = field.options.map((opt) => opt.value);
				if (isRequired) {
					const baseEnumSchema = z.enum(validValues as [string, ...string[]]);
					zodType = z.preprocess(
						(val) => (val === null ? "" : val),
						baseEnumSchema,
					);
				} else {
					// Allow empty string for optional select
					const baseUnionSchema = z.union([z.enum(validValues as [string, ...string[]]), z.literal("")]);
					zodType = z.preprocess(
						(val) => (val === null ? "" : val),
						baseUnionSchema,
					);
				}
			} else {
				// Dynamic options (options_source) - accept any string
				if (isRequired) {
					const baseStringSchema = z.string().min(1, "This field is required");
					zodType = z.preprocess(
						(val) => (val === null ? "" : val),
						baseStringSchema,
					);
				} else {
					const baseStringSchema = z.string();
					zodType = z.preprocess(
						(val) => (val === null ? "" : val),
						baseStringSchema,
					);
				}
			}
			break;
		}

		default: {
			// Fallback for unknown types
			zodType = z.unknown();
		}
	}

	// Handle optional fields - allow null and undefined
	if (!isRequired) {
		// For string-based types, also allow empty string
		if (field.type === "string" || field.type === "textarea" || field.type === "url") {
			zodType = z
				.union([zodType, z.literal("")])
				.optional()
				.nullable();
		} else if (field.type !== "select") {
			// Select already handles empty string above
			zodType = zodType.optional().nullable();
		} else {
			zodType = zodType.optional().nullable();
		}
	}

	return zodType;
}

/**
 * Generate a Zod object schema from a Record of SchemaFields (without conditions)
 */
export function generateZodSchema(schema: Record<string, SchemaField>): ZodObject<ZodRawShape> {
	const shape: Record<string, ZodTypeAny> = {};

	for (const [key, field] of Object.entries(schema)) {
		shape[key] = schemaFieldToZod(field, field.required);
	}

	// Use passthrough to allow additional fields not in the schema
	return z.object(shape as ZodRawShape).passthrough();
}

/**
 * Transform Zod error messages to be more user-friendly
 */
function transformErrorMessage(message: string, isRequired: boolean): string {
	// Transform "expected string, received null" to a more user-friendly message
	if (message.includes("expected string") && message.includes("received null")) {
		return isRequired ? "This field is required" : "This field cannot be empty";
	}
	// Transform "expected string, received undefined" to a more user-friendly message
	if (message.includes("expected string") && message.includes("received undefined")) {
		return isRequired ? "This field is required" : "This field cannot be empty";
	}
	return message;
}

/**
 * Validation result type
 */
export interface ValidationResult {
	success: boolean;
	errors: Record<string, string[]>;
}

/**
 * Validate a config object against a schema definition (without conditions)
 */
export function validateConfig(config: Record<string, unknown>, schema: Record<string, SchemaField>): ValidationResult {
	const zodSchema = generateZodSchema(schema);
	const result = zodSchema.safeParse(config);

	if (result.success) {
		return { success: true, errors: {} };
	}

	// Transform Zod errors into a more usable format
	const errors: Record<string, string[]> = {};
	for (const issue of result.error.issues) {
		const path = issue.path.map((p) => (typeof p === "string" || typeof p === "number" ? String(p) : "")).filter(Boolean).join(".");
		if (!errors[path]) {
			errors[path] = [];
		}
		// Check if field is required from schema
		const fieldName = issue.path[0];
		const field = fieldName && typeof fieldName === "string" ? schema[fieldName as string] : undefined;
		const isRequired = field?.required || false;
		errors[path].push(transformErrorMessage(issue.message, isRequired));
	}

	return { success: false, errors };
}

/**
 * Validate a single config with conditional logic
 */
function validateConfigWithConditions(config: Record<string, unknown>, schema: Record<string, SchemaField>, allConfigs: AllConfigs): ValidationResult {
	const errors: Record<string, string[]> = {};

	for (const [fieldName, field] of Object.entries(schema)) {
		// Skip validation for invisible fields
		if (!isFieldVisible(field, allConfigs)) {
			continue;
		}

		// Determine if field is required (static or conditional)
		const isRequired = isFieldRequired(field, allConfigs);

		// Generate Zod schema for this single field
		const fieldZodType = schemaFieldToZod(field, isRequired);
		const fieldValue = config[fieldName];

		// Validate the field
		const result = fieldZodType.safeParse(fieldValue);

		if (!result.success) {
			errors[fieldName] = result.error.issues.map((issue) => transformErrorMessage(issue.message, isRequired));
		}
	}

	return {
		success: Object.keys(errors).length === 0,
		errors,
	};
}

/**
 * Validate all configs for a wall of love template
 */
export interface WallOfLoveValidationInput {
	contentConfig: Record<string, unknown>;
	layoutConfig: Record<string, unknown>;
	seoConfig?: Record<string, unknown>;
}

export interface WallOfLoveValidationSchema {
	contentSchema: Record<string, SchemaField>;
	layoutSchema: Record<string, SchemaField>;
	seoSchema?: Record<string, SchemaField>;
}

export interface WallOfLoveValidationResult {
	isValid: boolean;
	content: ValidationResult;
	layout: ValidationResult;
	seo: ValidationResult;
}

export function validateWallOfLoveConfig(input: WallOfLoveValidationInput, schema: WallOfLoveValidationSchema): WallOfLoveValidationResult {
	// Combine all configs for condition evaluation
	const allConfigs: AllConfigs = {
		content: input.contentConfig,
		layout: input.layoutConfig,
		seo: input.seoConfig || {},
	};

	// Validate each schema with condition awareness
	const contentResult = validateConfigWithConditions(input.contentConfig, schema.contentSchema, allConfigs);
	const layoutResult = validateConfigWithConditions(input.layoutConfig, schema.layoutSchema, allConfigs);
	const seoResult = schema.seoSchema ? validateConfigWithConditions(input.seoConfig || {}, schema.seoSchema, allConfigs) : { success: true, errors: {} };

	return {
		isValid: contentResult.success && layoutResult.success && seoResult.success,
		content: contentResult,
		layout: layoutResult,
		seo: seoResult,
	};
}

/**
 * Get validation errors as a flat list for display
 */
export function getValidationErrorMessages(result: WallOfLoveValidationResult): string[] {
	const messages: string[] = [];

	const addErrors = (prefix: string, errors: Record<string, string[]>) => {
		for (const [field, fieldErrors] of Object.entries(errors)) {
			for (const error of fieldErrors) {
				messages.push(`${prefix}.${field}: ${error}`);
			}
		}
	};

	addErrors("content", result.content.errors);
	addErrors("layout", result.layout.errors);
	addErrors("seo", result.seo.errors);

	return messages;
}

/**
 * Check if a specific field has validation errors
 */
export function getFieldErrors(result: ValidationResult, fieldName: string): string[] {
	return result.errors[fieldName] || [];
}

/**
 * Helper to check if a field should be visible based on conditions
 * Useful for UI rendering decisions
 */
export function shouldFieldBeVisible(field: SchemaField, allConfigs: { content: Record<string, unknown>; layout: Record<string, unknown>; seo?: Record<string, unknown> }): boolean {
	return isFieldVisible(field, {
		content: allConfigs.content,
		layout: allConfigs.layout,
		seo: allConfigs.seo || {},
	});
}

/**
 * Helper to check if a field is required based on conditions
 * Useful for UI rendering decisions (showing required indicator)
 */
export function isFieldConditionallyRequired(field: SchemaField, allConfigs: { content: Record<string, unknown>; layout: Record<string, unknown>; seo?: Record<string, unknown> }): boolean {
	return isFieldRequired(field, {
		content: allConfigs.content,
		layout: allConfigs.layout,
		seo: allConfigs.seo || {},
	});
}

/**
 * Helper to check if a group should be visible based on conditions
 * Useful for UI rendering decisions
 */
export function shouldGroupBeVisible(group: SchemaGroup, allConfigs: { content: Record<string, unknown>; layout: Record<string, unknown>; seo?: Record<string, unknown> }): boolean {
	if (!group.conditions?.visible_if) {
		return true; // No conditions = always visible
	}
	return evaluateConditions(group.conditions.visible_if, {
		content: allConfigs.content,
		layout: allConfigs.layout,
		seo: allConfigs.seo || {},
	});
}
